import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import type { Outcome, OutcomeCategory, ImpactLevel, OutcomeStatus } from '../../types/outcomes';

interface EditOutcomeModalProps {
  outcome: Outcome;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: Partial<Outcome>) => void;
}

export const EditOutcomeModal: React.FC<EditOutcomeModalProps> = ({
  outcome,
  isOpen,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState(outcome.title);
  const [description, setDescription] = useState(outcome.description);
  const [status, setStatus] = useState<OutcomeStatus>(outcome.status);
  const [category, setCategory] = useState<OutcomeCategory>(outcome.category);
  const [impactLevel, setImpactLevel] = useState<ImpactLevel>(outcome.impactLevel);
  const [targetDate, setTargetDate] = useState(outcome.targetDate || '');

  useEffect(() => {
    if (isOpen) {
      setTitle(outcome.title);
      setDescription(outcome.description);
      setStatus(outcome.status);
      setCategory(outcome.category);
      setImpactLevel(outcome.impactLevel);
      setTargetDate(outcome.targetDate || '');
    }
  }, [isOpen, outcome]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !description.trim()) return;

    onSave({
      title: title.trim(),
      description: description.trim(),
      status,
      category,
      impactLevel,
      targetDate: targetDate || undefined,
    });

    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-sage-500 p-6 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Edit Outcome</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-[var(--bg-raised)]/20 transition-colors"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <div className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Outcome Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                placeholder="e.g., Implement DEGAP for Q1 Roadmap"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Description *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none resize-none"
                placeholder="Describe what you want to achieve..."
                required
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as OutcomeStatus)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
              >
                <option value="planning">Planning</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="on-hold">On Hold</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {/* Category and Impact Level */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as OutcomeCategory)}
                  className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                >
                  <option value="strategic">Strategic</option>
                  <option value="operational">Operational</option>
                  <option value="team">Team</option>
                  <option value="personal">Personal</option>
                  <option value="project">Project</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                  Impact Level
                </label>
                <select
                  value={impactLevel}
                  onChange={(e) => setImpactLevel(e.target.value as ImpactLevel)}
                  className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
                >
                  <option value="low">Low Impact</option>
                  <option value="medium">Medium Impact</option>
                  <option value="high">High Impact</option>
                  <option value="transformational">Transformational</option>
                </select>
              </div>
            </div>

            {/* Target Date */}
            <div>
              <label className="block text-sm font-semibold text-charcoal-900 mb-2">
                Target Date (Optional)
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full px-4 py-2 border-2 border-[var(--rule)] focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-[var(--rule)] bg-[var(--surface)] flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border-2 border-[var(--rule)] text-charcoal-700 hover:bg-[var(--hover-tint)] font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-primary-600 text-white hover:bg-primary-700 font-medium transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

